import { definePartner } from "../definePartner";

/**
 * DotPe: the first-party loyalty partner (legacy class 3).
 *
 * 🔴 **`isExternalLoyalty` has been `true` since 2026-09-09 (DM-146).** Before, a DotPe CRM coupon
 * travelled the ORDINARY coupon path and came off the bill without ever being released at the
 * partner; `requiresCouponUnblock` moved with it.
 *
 * The narrower class-1 guard inside the release (reference id AND phone both present) is
 * `unblockRequiresReferenceAndPhone`, which stays `false` here; `hasCouponModal` stays `false`
 * too: an offers leg is not the coupon modal.
 *
 * 🔴 **DotPe asks the customer's OTP when a listed offer is applied (decision 115).** The backend's
 * `requestRedeem` always answers `{ authenticate: true, ...the checked coupon }`, and the redeem
 * that follows (`redeemOffer`, through `POST /sale/customer/offer/redeem`) needs the OTP
 * (api_dotpe_service.js 1011-1101). Web v1 asked it on its plain path (`sendApplyRequest` opens the
 * prompt whenever the answer has a `redemptionInfo`) and skipped it on its replace path; v2 asks on
 * both. `backend.applyOtp: "skipped"` is a different fact: the backend's CUSTOMER check inside
 * `/sale/customer/offer/redeem` does not run for DotPe, which says nothing about the OTP on the redeem.
 */
export default definePartner("dotpe", {
  name: "DotPe",
  backend: {
    listsOffers: true,
    appliesOffers: true,
    applyOtp: "skipped",
    takesTypedCodes: true,
    releaseNeeds: ["customerPhone", "couponCode", "brandId", "branchId"],
    exercisedAtSave: false,
  },

  sendsRequestId: true,
  isExternalLoyalty: true,
  // The apply asks the customer's OTP (decision 115, the header). The verify that follows needs no field
  // of its own: the baseline's are v1's `externalDiscountOtpValidate` for DotPe (a flat body to
  // `offer-verify`, `message === "success"`, the customer stamped as authenticated by the partner).
  // `honoursOfferOtpFlag` stays false: v1 honours an offer's `isOtpRequired` for Xeno alone.
  requiresOtpOnApply: true,
  requiresCouponUnblock: true,
  // A DotPe redeem or release changes the customer's balance and milestones at DotPe (v1's loyalty
  // saga refetched the customer after each, `refreshCustomerAfterLoyaltyReward`).
  refreshesCustomerRecord: true,
  // v1 released DotPe's coupon, and no other partner's, when the bill changed under it (decision 62).
  releasesOnBillChange: true,
  // Its redeem request answers a `blockId`: the points are held at DotPe until the OTP is verified or the hold released (api_dotpe_service.js 943-947).
  holdsPointsOnRequest: true,
  // The only partner whose adapter defines `customerOptOut` (api_dotpe_service.js 1361-1452; decision 54).
  optsOut: true,
  // The backend defines `refundLoyaltyPoints` for it (points-conversations.md §2).
  refundsPoints: true,
  // A typed code is redeemed with the customer's OTP (`coupon/redeem`; old FE ItemEditController.js 1441-1465). Nothing is blocked while it is asked, and DotPe has no resend.
  codeNeedsOtp: true,
});
